import { fetchGenerationHistoryImageBlob } from '@/services/generationHistory.service';
import {
  GH_DRAG_MIME_TEXT,
  buildGhImagePayload,
  clearGhImageDragSession,
  dataTransferHasGhMime,
  isDesignGenerationRoute,
  readGhDragPayload,
  resolveGhDragImageToDataUrl,
  setGhImageDragData,
} from '@/shared/helpers/generationHistoryDrag';
import {
  buildGhAssistTextPayload,
  clearGhTextDragSession,
  setGhAssistTextDragData,
} from '@/shared/helpers/generationHistoryAssistDrag';

/**
 * Drag-source helpers for generation-history detail cards.
 * Expects `this.card` with at least `item_id`.
 */
export const generationHistoryCardDragMixin = {
  computed: {
    canDrag() {
      return isDesignGenerationRoute(this.$route);
    },
  },
  methods: {
    /**
     * @param {DragEvent} event
     * @param {{ text: string, sourceType: string, itemId?: *, field?: string }} options
     */
    startGhTextDrag(event, { text, sourceType, itemId, field }) {
      const value = text || '';
      if (!this.canDrag || !value) {
        event.preventDefault();
        return;
      }
      const resolvedItemId = itemId == null
        ? (this.card && this.card.item_id)
        : itemId;
      setGhAssistTextDragData(event.dataTransfer, buildGhAssistTextPayload({
        text: value,
        itemId: resolvedItemId,
        sourceType,
        field,
      }));
      event.currentTarget.classList.add('gh-dnd-source--dragging');
    },
    /**
     * @param {DragEvent} event
     * @param {{
     *   url: string,
     *   sourceType: string,
     *   itemId?: *,
     *   itemType?: *,
     *   posterIndex?: number,
     * }} options
     */
    startGhImageDrag(event, {
      url,
      sourceType,
      itemId,
      itemType,
      posterIndex,
    }) {
      if (!this.canDrag || !url) {
        event.preventDefault();
        return;
      }
      const resolvedItemId = itemId == null
        ? (this.card && this.card.item_id)
        : itemId;
      setGhImageDragData(
        event.dataTransfer,
        buildGhImagePayload({
          url,
          itemId: resolvedItemId,
          itemType: itemType == null ? null : itemType,
          posterIndex: posterIndex == null ? 0 : posterIndex,
          sourceType,
        }),
        { element: event.currentTarget },
      );
      event.currentTarget.classList.add('gh-dnd-source--dragging');
    },
    onDragEnd(event) {
      if (event && event.currentTarget) {
        event.currentTarget.classList.remove('gh-dnd-source--dragging');
      }
      clearGhImageDragSession();
      clearGhTextDragSession();
    },
  },
};

/**
 * Drop-target helpers for デザイン生成 tabs.
 * - Prompt: onPromptDragOver / onPromptDrop → replaces this.prompt
 * - Image: resolveGhImageDrop(payload) → data URL (caller applies to its upload slot)
 */
export const generationHistoryDropMixin = {
  methods: {
    onPromptDragOver(event) {
      if (dataTransferHasGhMime(event.dataTransfer, GH_DRAG_MIME_TEXT)) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    onPromptDrop(event) {
      const payload = readGhDragPayload(event.dataTransfer);
      if (!payload || payload.kind !== 'text') {
        return;
      }
      const next = payload.text || '';
      if (!next) {
        return;
      }
      this.prompt = next;
    },
    /**
     * @param {object} payload
     * @returns {Promise<string|null>}
     */
    async resolveGhImageDrop(payload) {
      if (this.generating) {
        return null;
      }
      try {
        return await resolveGhDragImageToDataUrl(
          payload,
          fetchGenerationHistoryImageBlob,
        );
      } catch (e) {
        alert(this.$t('generation_history.drop_image_error'));
        return null;
      }
    },
  },
};
